import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';

import { ModuleGradient } from '../../../config/designSystem/Colors';
import { QuestionSessionBodyContainerProps } from '../../QuestionSessions/QuestionSessionBody/config';
import { useDiaryFrame } from '../useDiaryFrame';
import { useReferentialQuestionContent } from '../../../hooks/content/useReferentialQuestionContent';
import { useStores } from '../../../hooks/useStores';

export interface AssessmentData extends Record<string, unknown> {
  /** answer wordings, worst → best */
  scale: string[];
  value: number;
  onChangeValue: (value: number) => void;
  gradient: ModuleGradient;
}

/**
 * Wires the assessment frame to the stores: the scale comes from the
 * question's answers, the value is kept in the pending response (with the
 * matching answer + wording so entries stay readable).
 */
const _ContainAssessment: FC<QuestionSessionBodyContainerProps> = ({
  question,
  children,
}) => {
  const { treeNav } = useStores();
  const { question: questionStore } = useStores();
  const loading = !questionStore.isReady(question._id);
  const { description, question: questionText } = useReferentialQuestionContent(question);
  const { moduleGradient } = useDiaryFrame();

  const answers = question?.answers?.slice() || [];
  const steps = Math.max(answers.length, 2);
  const middle = Math.ceil((steps - 1) / 2);

  const pendingValue = treeNav.choices.pending?.value;
  const value = typeof pendingValue === 'number' ? pendingValue : middle;

  const onChangeValue = (newValue: number) => {
    const answer = answers[newValue];
    treeNav.choices.updatePending({
      questionId: question._id,
      questionKey: question.key,
      // stamp the current context: a stale pending from an unfinished
      // session would otherwise leak its ids into this response
      surveyId: treeNav.content.survey?._id,
      pathId: treeNav.content.path?._id,
      sessionId: treeNav.content.session?._id,
      value: newValue,
      freeText: answer?.wording || String(newValue),
      answers: answer ? [{ id: answer._id, key: answer.key || null, meta: null }] : [],
    });
  };

  // initialise with the middle value so the frame always saves an answer
  useEffect(() => {
    if (question?._id && typeof treeNav.choices.pending?.value !== 'number') {
      onChangeValue(middle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?._id]);

  const data: AssessmentData = {
    scale: answers.map(answer => answer.wording || ''),
    value,
    onChangeValue,
    gradient: moduleGradient,
  };

  return children({
    questionId: question._id,
    questionKey: question.key,
    questionType: question.type,
    questionText,
    questionDescription: description,
    loading,
    data,
  });
};

export const ContainAssessment = observer(_ContainAssessment);
